import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useThreadContext } from "../../context/ThreadContext";
import { useThreadNotificationIntegration } from "../../hooks/notifications/useThreadNotificationIntegration";
import threadApi from "../../api/threadApi";
import PostForm from "./PostForm";
import MessageList from "./MessageList";
// Lazy loading utilities available for future use
// import { LazyReplies, useIntersectionObserver } from "../../utils/thread/lazyLoading";

/**
 * Thread View Component
 * Main container for displaying fight discussion threads
 * Requirements: 2.1, 2.3, 7.1, 7.5
 *
 * @param {Object} props - Component props
 * @param {string} props.threadId - The thread ID to display
 * @param {number} props.fightId - Optional fight ID for context
 * @param {Object} props.initialThreadData - Optional initial thread data from navigation
 * @param {string} props.highlightMessageId - Optional message ID to highlight (from notifications)
 * @param {string} props.highlightReplyId - Optional reply ID to highlight (from notifications)
 * @param {Function} props.onError - Optional error handler callback
 * @param {Function} props.onThreadLoad - Optional callback when thread loads
 */
const ThreadView = ({
  threadId,
  fightId,
  initialThreadData,
  highlightMessageId,
  highlightReplyId,
  onError,
  onThreadLoad,
}) => {
  const { user } = useAuth0();
  const {
    currentThread,
    fight,
    event,
    metadata,
    messages,
    loading,
    error,
    loadFightThread,
    postMessage,
    loadMessages,
    setCurrentThread,
    clearError,
    retryLastOperation,
  } = useThreadContext();

  // Initialize real-time thread notifications
  useThreadNotificationIntegration();

  // Ref for scrolling to top when new messages are posted
  const messagesAreaRef = useRef(null);

  // Pull-to-refresh state
  const [pullToRefreshState, setPullToRefreshState] = useState({
    isPulling: false,
    pullDistance: 0,
    threshold: 80,
  });

  // Local state for component-specific data
  const [localState, setLocalState] = useState({
    initialized: false,
    retryCount: 0,
    messagesLoaded: false,
    isRefreshing: false,
    lastRefresh: null,
  });

  /**
   * Initialize thread data and messages simultaneously
   */
  const initializeThread = useCallback(async () => {
    if (localState.initialized) return;

    // If we have initial thread data, use it
    if (initialThreadData) {
      setCurrentThread(initialThreadData);
      setLocalState((prev) => ({ ...prev, initialized: true }));
      onThreadLoad?.(initialThreadData);

      // Load messages using the thread ID from initial data
      if (initialThreadData.thread?.id) {
        try {
          await loadMessages(1, 10, initialThreadData.thread.id);
          setLocalState((prev) => ({ ...prev, messagesLoaded: true }));
        } catch (err) {
          console.warn("Failed to load messages:", err);
          setLocalState((prev) => ({ ...prev, messagesLoaded: true }));
        }
      }
      return;
    }

    // If we have a fightId, load the thread and messages simultaneously
    if (fightId) {
      try {
        const response = await loadFightThread(fightId);
        setLocalState((prev) => ({
          ...prev,
          initialized: true,
          retryCount: 0,
        }));
        onThreadLoad?.(response);

        // Load messages immediately after thread loads
        if (response.thread?.id) {
          try {
            // Pass the thread ID directly to avoid race condition
            await loadMessages(1, 10, response.thread.id);
            setLocalState((prev) => ({ ...prev, messagesLoaded: true }));
          } catch (err) {
            console.warn("Failed to load messages:", err);
            setLocalState((prev) => ({ ...prev, messagesLoaded: true }));
          }
        }
      } catch (err) {
        setLocalState((prev) => ({ ...prev, retryCount: prev.retryCount + 1 }));
        onError?.(err);
      }
      return;
    }

    // If we only have threadId but no fightId, show an error for now
    // In the future, this could be enhanced to support direct thread access
    if (threadId && !fightId && !initialThreadData) {
      const error = new Error(
        "Unable to load thread - missing fight context. Please navigate from a fight card or notification with complete information."
      );
      onError?.(error);
      return;
    }

    // No valid parameters
    const error = new Error("Either threadId or fightId is required");
    onError?.(error);
  }, [
    fightId,
    threadId,
    initialThreadData,
    loadFightThread,
    loadMessages,
    setCurrentThread,
    onError,
    onThreadLoad,
    localState.initialized,
  ]);

  /**
   * Handle message posting
   */
  const handlePostMessage = useCallback(
    async (messageData) => {
      if (!currentThread?.id) {
        throw new Error("No active thread to post to");
      }

      // Add threadId to message data
      const fullMessageData = {
        ...messageData,
        threadId: currentThread.id,
      };

      // Post the message
      const postedMessage = await postMessage(fullMessageData);

      // Scroll to top to show the new message
      setTimeout(() => {
        if (messagesAreaRef.current) {
          messagesAreaRef.current.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }, 100);

      return postedMessage;
    },
    [currentThread?.id, postMessage]
  );

  /**
   * Refresh thread messages
   */
  const refreshMessages = useCallback(async () => {
    if (localState.isRefreshing || !currentThread?.id) {
      console.log("🔄 Refresh blocked:", {
        isRefreshing: localState.isRefreshing,
        hasThread: !!currentThread?.id,
      });
      return;
    }

    console.log("🔄 Starting refresh for thread:", currentThread.id);
    setLocalState((prev) => ({ ...prev, isRefreshing: true }));

    try {
      // Clear cache for this thread to ensure fresh data
      console.log("🗑️ Clearing cache for thread:", currentThread.id);
      threadApi.invalidateThreadCache(currentThread.id);

      // Also clear message cache to be extra sure
      console.log("🗑️ Clearing all message cache");
      threadApi.clearCache();

      // Reload messages for the current thread with cache bypass
      console.log("🔄 Calling loadMessages with bypassCache=true");
      const result = await loadMessages(1, 10, currentThread.id, true); // bypassCache = true
      console.log("🔄 loadMessages result:", result);
      console.log(
        "🔄 Messages in result:",
        result?.messages?.map((m) => ({
          id: m._id,
          content: m.content.substring(0, 50),
        }))
      );

      setLocalState((prev) => ({
        ...prev,
        isRefreshing: false,
        lastRefresh: Date.now(),
      }));

      // Show success feedback
      console.log(
        `✅ Thread refreshed successfully - found ${
          result?.messages?.length || 0
        } messages`
      );

      // Show a brief success message
      const successMessage = document.createElement("div");
      successMessage.textContent = `Thread refreshed! Found ${
        result?.messages?.length || 0
      } messages`;
      successMessage.className =
        "fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50";
      document.body.appendChild(successMessage);
      setTimeout(() => {
        document.body.removeChild(successMessage);
      }, 2000);
    } catch (error) {
      console.error("❌ Failed to refresh thread:", error);
      setLocalState((prev) => ({ ...prev, isRefreshing: false }));

      // Show error message
      const errorMessage = document.createElement("div");
      errorMessage.textContent = "Failed to refresh thread";
      errorMessage.className =
        "fixed top-20 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50";
      document.body.appendChild(errorMessage);
      setTimeout(() => {
        document.body.removeChild(errorMessage);
      }, 3000);
    }
  }, [currentThread?.id, loadMessages, localState.isRefreshing]);

  /**
   * Handle pull-to-refresh functionality
   */
  const handleTouchStart = useCallback((e) => {
    if (messagesAreaRef.current && messagesAreaRef.current.scrollTop === 0) {
      const touch = e.touches[0];
      setPullToRefreshState((prev) => ({
        ...prev,
        startY: touch.clientY,
        isPulling: false,
        pullDistance: 0,
      }));
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (messagesAreaRef.current && messagesAreaRef.current.scrollTop === 0) {
      const touch = e.touches[0];
      setPullToRefreshState((prev) => {
        if (prev.startY && touch.clientY > prev.startY) {
          const pullDistance = Math.min(
            touch.clientY - prev.startY,
            prev.threshold * 1.5
          );
          return {
            ...prev,
            isPulling: pullDistance > 20,
            pullDistance,
          };
        }
        return prev;
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    setPullToRefreshState((prev) => {
      if (prev.pullDistance >= prev.threshold && !localState.isRefreshing) {
        // Trigger refresh
        refreshMessages();
      }
      return {
        ...prev,
        isPulling: false,
        pullDistance: 0,
        startY: null,
      };
    });
  }, [refreshMessages, localState.isRefreshing]);

  /**
   * Handle message posting errors
   */
  const handlePostError = useCallback((error) => {
    // Error is already handled by the PostForm component
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeThread();
  }, [initializeThread]);

  /**
   * Handle highlighted message loading after messages are loaded
   */
  useEffect(() => {
    if (
      highlightMessageId &&
      localState.messagesLoaded &&
      messages.size > 0 &&
      currentThread?.id
    ) {
      // Check if the highlighted message exists in the loaded messages
      const messageExists = Array.from(messages.values()).some(
        (msg) => msg._id === highlightMessageId
      );

      if (!messageExists) {
        console.log(
          "⚠️ Highlighted message not found in current messages:",
          highlightMessageId
        );
        console.log(
          "📋 Available messages:",
          Array.from(messages.values()).map((msg) => ({
            id: msg._id,
            content: msg.content?.substring(0, 50) + "...",
            replyCount: msg.replyCount,
            hasReplies: msg.replyCount > 0,
          }))
        );

        console.log(
          "💡 The message may be on a different page. Consider implementing server-side search or manual pagination."
        );
      } else {
        console.log(
          "✅ Highlighted message found in loaded messages:",
          highlightMessageId
        );
      }
    }

    // Enhanced debugging for reply highlighting
    if (
      highlightReplyId &&
      localState.messagesLoaded &&
      messages.size > 0 &&
      currentThread?.id
    ) {
      console.log("🔍 Searching for highlighted reply:", highlightReplyId);

      // Check all messages and their replies
      let replyFound = false;

      Array.from(messages.values()).forEach((message) => {
        console.log(`📝 Checking message ${message._id} for replies...`);

        // Check if this message has replies
        if (message.replyCount > 0) {
          console.log(`  📬 Message has ${message.replyCount} replies`);
          // Note: We can't check individual replies here since they're loaded separately
          // The ReplyList component will handle the actual reply highlighting
        }
      });

      if (!replyFound) {
        console.log(
          "⚠️ Reply search completed - reply highlighting will be handled by ReplyList components"
        );
        console.log("🔍 Reply ID being searched:", highlightReplyId);
      }
    }
  }, [
    highlightMessageId,
    highlightReplyId,
    localState.messagesLoaded,
    messages.size,
    currentThread?.id,
    messages,
  ]);

  /**
   * Retry loading thread
   */
  const retryLoad = useCallback(async () => {
    try {
      if (fightId) {
        await loadFightThread(fightId);
        setLocalState((prev) => ({ ...prev, retryCount: 0 }));
      } else {
        await retryLastOperation();
        setLocalState((prev) => ({ ...prev, retryCount: 0 }));
      }
    } catch (err) {
      setLocalState((prev) => ({ ...prev, retryCount: prev.retryCount + 1 }));
      onError?.(err);
    }
  }, [fightId, loadFightThread, retryLastOperation, onError]);

  /**
   * Clear error state
   */
  const handleClearError = useCallback(() => {
    clearError();
  }, [clearError]);

  /**
   * Format date for display
   */
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  }, []);

  /**
   * Get thread status display
   */
  const getThreadStatus = useCallback(() => {
    if (!currentThread) return null;

    const { status, isOfficial, messageCount, participantCount } =
      currentThread;

    return {
      status: status || "active",
      isOfficial: Boolean(isOfficial),
      messageCount: parseInt(messageCount) || 0,
      participantCount: parseInt(participantCount) || 0,
    };
  }, [currentThread]);

  // Render loading state
  if (loading.thread) {
    return (
      <div className="relative h-screen w-full bg-light-grey overflow-hidden">
        {/* Loading Header */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
          <div className="animate-pulse">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 min-w-0">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="flex-1 flex items-center justify-center pb-24">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-deep-red mb-4"></div>
            <p className="text-medium-grey">Loading thread...</p>
          </div>
        </div>

        {/* Message Input - Fixed at bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
          <div className="flex items-end space-x-3 opacity-50">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">U</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <textarea
                rows={1}
                disabled
                className="block w-full resize-none border border-gray-300 rounded-card px-3 py-2 text-sm placeholder-medium-grey bg-gray-100"
                placeholder="Loading..."
                style={{ minHeight: "40px" }}
              />
            </div>
            <div className="flex-shrink-0">
              <button
                disabled
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-card text-white bg-gray-400"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="relative h-screen w-full bg-light-grey overflow-hidden">
        {/* Error Header */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-deep-red"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-dark-grey truncate">
                Thread Error
              </h1>
              <p className="text-sm text-medium-grey truncate">
                Unable to load discussion
              </p>
            </div>
          </div>
        </div>

        {/* Error Content */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-24">
          <div className="text-center max-w-md w-full">
            <div className="text-deep-red text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-dark-grey mb-2">
              Unable to Load Thread
            </h3>
            <p className="text-medium-grey mb-6 break-words">{error}</p>

            <div className="flex justify-center space-x-4">
              <button
                onClick={retryLoad}
                className="px-4 py-2 bg-deep-red text-white rounded-card hover:bg-bright-red focus:outline-none focus:ring-2 focus:ring-deep-red focus:ring-offset-2 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleClearError}
                className="px-4 py-2 bg-gray-300 text-medium-grey rounded-card hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Dismiss
              </button>
            </div>

            {localState.retryCount > 0 && (
              <p className="text-sm text-medium-grey mt-4">
                Retry attempts: {localState.retryCount}
              </p>
            )}
          </div>
        </div>

        {/* Message Input - Fixed at bottom (disabled) */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
          <div className="flex items-end space-x-3 opacity-50">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">U</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <textarea
                rows={1}
                disabled
                className="block w-full resize-none border border-gray-300 rounded-card px-3 py-2 text-sm placeholder-medium-grey bg-gray-100"
                placeholder="Unable to send messages"
                style={{ minHeight: "40px" }}
              />
            </div>
            <div className="flex-shrink-0">
              <button
                disabled
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-card text-white bg-gray-400"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render thread content
  const threadStatus = getThreadStatus();

  return (
    <div className="fixed inset-0 bg-light-grey flex flex-col pb-16">
      {/* Thread Header - Fixed at top */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            {/* Thread Icon */}
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-deep-red rounded-card flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            {/* Thread Title and Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h1 className="text-lg font-semibold text-dark-grey truncate">
                  {metadata?.fightTitle ||
                    currentThread?.title ||
                    "Fight Discussion"}
                </h1>
                {threadStatus?.isOfficial && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-card text-xs font-medium bg-light-gold text-gold flex-shrink-0">
                    Official
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm text-medium-grey truncate">
                <span className="flex-shrink-0">
                  {threadStatus?.participantCount || 0} members
                </span>
                {metadata?.eventTitle && (
                  <>
                    <span className="flex-shrink-0">•</span>
                    <span className="truncate">{metadata.eventTitle}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Thread Actions */}
          <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
            {/* Refresh Button - Made more prominent */}
            <button
              onClick={refreshMessages}
              disabled={localState.isRefreshing}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-deep-red hover:bg-bright-red disabled:bg-gray-400 rounded-card transition-colors disabled:opacity-50"
              title="Refresh thread"
            >
              <svg
                className={`w-4 h-4 ${
                  localState.isRefreshing ? "animate-spin" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span className="hidden sm:inline">
                {localState.isRefreshing ? "Refreshing..." : "Refresh"}
              </span>
            </button>

            {/* Other action buttons */}
            <button className="p-2 text-medium-grey hover:text-deep-red hover:bg-gray-100 rounded-card transition-colors">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Fight Details Banner */}
        {fight && fight.Fighters && fight.Fighters.length >= 2 && (
          <div className="mt-3 p-3 bg-light-grey rounded-card border border-gray-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="text-center flex-1 min-w-0">
                <div className="font-medium text-dark-grey truncate">
                  {fight.Fighters[0].Name ||
                    `${fight.Fighters[0].FirstName} ${fight.Fighters[0].LastName}`.trim() ||
                    "Fighter 1"}
                </div>
                {fight.Fighters[0].WeightClass && (
                  <div className="text-xs text-medium-grey truncate">
                    {fight.Fighters[0].WeightClass}
                  </div>
                )}
              </div>
              <div className="text-lg font-bold text-deep-red flex-shrink-0">
                VS
              </div>
              <div className="text-center flex-1 min-w-0">
                <div className="font-medium text-dark-grey truncate">
                  {fight.Fighters[1].Name ||
                    `${fight.Fighters[1].FirstName} ${fight.Fighters[1].LastName}`.trim() ||
                    "Fighter 2"}
                </div>
                {fight.Fighters[1].WeightClass && (
                  <div className="text-xs text-medium-grey truncate">
                    {fight.Fighters[1].WeightClass}
                  </div>
                )}
              </div>
            </div>
            {event?.dateTime && (
              <div className="text-center text-xs text-medium-grey mt-2 truncate">
                {formatDate(event.dateTime)}
              </div>
            )}
          </div>
        )}

        {/* New Thread Indicator */}
        {metadata?.isNewThread && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-card">
            <div className="flex items-center">
              <svg
                className="h-4 w-4 text-green-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-green-800 truncate">
                New discussion thread created for this fight!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Messages Area - Scrollable content with pull-to-refresh */}
      <div
        ref={messagesAreaRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 relative"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: pullToRefreshState.isPulling
            ? `translateY(${Math.min(
                pullToRefreshState.pullDistance,
                pullToRefreshState.threshold
              )}px)`
            : "none",
          transition: pullToRefreshState.isPulling
            ? "none"
            : "transform 0.3s ease-out",
        }}
      >
        {/* Pull-to-refresh indicator */}
        {pullToRefreshState.isPulling && (
          <div
            className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-blue-600 text-white px-4 py-2 rounded-b-lg shadow-lg z-20 flex items-center space-x-2"
            style={{
              opacity: Math.min(
                pullToRefreshState.pullDistance / pullToRefreshState.threshold,
                1
              ),
            }}
          >
            <svg
              className={`w-4 h-4 ${
                pullToRefreshState.pullDistance >= pullToRefreshState.threshold
                  ? "animate-spin"
                  : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="text-sm font-medium">
              {pullToRefreshState.pullDistance >= pullToRefreshState.threshold
                ? "Release to refresh"
                : "Pull to refresh"}
            </span>
          </div>
        )}

        {/* Refresh status indicator */}
        {localState.isRefreshing && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg z-20 flex items-center space-x-2">
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="text-sm font-medium">Refreshing...</span>
          </div>
        )}
        <div className="space-y-4 max-w-none">
          {/* Quick refresh button at top of messages */}
          <div className="flex justify-center">
            <button
              onClick={refreshMessages}
              disabled={localState.isRefreshing}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-deep-red bg-white border border-deep-red hover:bg-deep-red hover:text-white disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 rounded-full transition-colors shadow-sm"
            >
              <svg
                className={`w-4 h-4 ${
                  localState.isRefreshing ? "animate-spin" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>
                {localState.isRefreshing
                  ? "Refreshing..."
                  : "Check for new messages"}
              </span>
            </button>
          </div>
          {/* Welcome Message */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-deep-red rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-dark-grey">
                  System
                </span>
                <span className="text-xs text-medium-grey">
                  {formatDate(currentThread?.createdAt)}
                </span>
              </div>
              <div className="mt-1">
                <div className="bg-white rounded-card shadow-card p-3 border border-gray-200">
                  <p className="text-sm text-dark-grey break-words">
                    Welcome to the fight discussion! This thread was created for{" "}
                    <span className="font-medium text-deep-red">
                      {metadata?.fightTitle || "this fight"}
                    </span>
                    . Share your thoughts, predictions, and reactions here.
                  </p>
                  <p className="text-xs text-medium-grey mt-2">
                    💡 Tip: Pull down from the top or use the refresh button to
                    check for new messages from other users.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Messages List */}
          {currentThread?.id && (
            <>
              {loading.messages && !localState.messagesLoaded ? (
                // Show loading state while messages are being fetched
                <div className="flex justify-center items-center py-8">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-deep-red"></div>
                    <span className="text-sm text-medium-grey">
                      Loading messages...
                    </span>
                  </div>
                </div>
              ) : (
                // Show MessageList only after loading is complete
                <MessageList
                  threadId={currentThread.id}
                  messages={Array.from(messages.values())}
                  loading={false} // Always false since we handle loading above
                  currentUser={user}
                  highlightMessageId={highlightMessageId}
                  highlightReplyId={highlightReplyId}
                  onLoadMore={() => {}} // Will be implemented with pagination
                  onMessageUpdate={() => {}} // Will be implemented with real-time updates
                  disableAutoLoad={true} // Prevent MessageList from making its own API calls
                  useVirtualization={true} // Enable virtualization for performance
                  virtualizationThreshold={30} // Use virtualization when 30+ messages
                  containerHeight={500} // Set container height for virtualization
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Message Input - Fixed at bottom, above mobile nav */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white shadow-header px-4 sm:px-6 py-4">
        {currentThread?.id ? (
          <PostForm
            threadId={currentThread.id}
            onSubmit={handlePostMessage}
            onError={handlePostError}
            placeholder="Write a message..."
            maxLength={500}
            disabled={loading.posting}
            className="w-full"
          />
        ) : (
          <div className="flex items-end space-x-3 opacity-50">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">U</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <textarea
                rows={1}
                disabled
                className="block w-full resize-none border border-gray-300 rounded-card px-3 py-2 text-sm placeholder-medium-grey bg-gray-100"
                placeholder="Unable to send messages"
                style={{ minHeight: "40px" }}
              />
            </div>
            <div className="flex-shrink-0">
              <button
                disabled
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-card text-white bg-gray-400"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadView;
